/**
 * The games of stages 1–2 with the path on and the stickers they earned as ONE JSON value (shared `PathPlayDoc`, the
 * browser's `gambit.path.play`, docs/PATH-V2.md §7.1, §7.4, §10, §12.5 «kv — наклейки») — its parse and its merge, pure,
 * for the web store (apps/web/src/features/path/play/playStore.ts) AND the account's kv (apps/server/src/routes/
 * accountState.ts): two devices of one child never lose a fact or a sticker, whichever copy reaches the server last.
 *
 * Facts merge as unions (a game by `gameId` — the first copy wins, facts; a sticker by `id` — the earliest earned wins,
 * a sticker is never taken away); a broken or foreign value reads as an empty doc.
 */
import type { PathPlayDoc, PathPlayedFact, PathSticker } from '@gambit/shared';

/** The stored played games are capped: the ladder reads its own last step, the habit's fade saturates by ~24 games. */
export const PLAYED_KEEP = 120;

export function emptyPlayDoc(): PathPlayDoc {
  return { v: 1 };
}

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;
const HELPS: readonly unknown[] = ['teacher', 'quiet', 'helper', 'exam'];
const LADDER: readonly unknown[] = ['teacher', 'quiet', 'helper'];

function isPlayedFact(value: unknown): value is PathPlayedFact {
  if (typeof value !== 'object' || value === null) return false;
  const p = value as Partial<PathPlayedFact>;
  return (
    typeof p.gameId === 'string' &&
    p.gameId !== '' &&
    HELPS.includes(p.help) &&
    typeof p.habit === 'boolean' &&
    typeof p.finished === 'boolean' &&
    typeof p.won === 'boolean' &&
    (p.ladder === undefined || LADDER.includes(p.ladder)) &&
    [p.at, p.giftsTaken, p.giftChances, p.ownHung, p.gifts, p.safeChecks, p.mates, p.hungAll, p.moves].every(isCount)
  );
}

function isSticker(value: unknown): value is PathSticker {
  if (typeof value !== 'object' || value === null) return false;
  const s = value as Partial<PathSticker>;
  return typeof s.id === 'string' && s.id !== '' && isCount(s.at);
}

/** Union by `gameId` (the first copy wins — facts), oldest first, the newest `PLAYED_KEEP`. */
function mergePlayed(a: readonly PathPlayedFact[], b: readonly PathPlayedFact[]): PathPlayedFact[] {
  const byId = new Map<string, PathPlayedFact>();
  for (const p of [...a, ...b]) if (!byId.has(p.gameId)) byId.set(p.gameId, p);
  return [...byId.values()].sort((x, y) => x.at - y.at).slice(-PLAYED_KEEP);
}

/** Union by `id` — the earliest earned wins (a sticker is never taken away), first earned first. */
function mergeStickers(a: readonly PathSticker[], b: readonly PathSticker[]): PathSticker[] {
  const byId = new Map<string, PathSticker>();
  for (const s of [...a, ...b]) {
    const seen = byId.get(s.id);
    if (!seen || s.at < seen.at) byId.set(s.id, s);
  }
  return [...byId.values()].sort((x, y) => x.at - y.at);
}

/** A stored value → a clean doc (junk entries dropped); anything broken → empty. */
export function parsePlayDoc(raw: string | null | undefined): PathPlayDoc {
  if (raw === null || raw === undefined) return emptyPlayDoc();
  try {
    const value = JSON.parse(raw) as Partial<PathPlayDoc> | null;
    const doc = emptyPlayDoc();
    if (typeof value !== 'object' || value === null) return doc;
    if (Array.isArray(value.played)) {
      const played = mergePlayed(value.played.filter(isPlayedFact), []);
      if (played.length > 0) doc.played = played;
    }
    if (Array.isArray(value.stickers)) {
      const stickers = mergeStickers(value.stickers.filter(isSticker), []);
      if (stickers.length > 0) doc.stickers = stickers;
    }
    return doc;
  } catch {
    return emptyPlayDoc();
  }
}

/** Two copies (this tab and another, or the account's) joined as unions. */
export function mergePlayDocs(a: PathPlayDoc, b: PathPlayDoc): PathPlayDoc {
  const doc = emptyPlayDoc();
  if (a.played || b.played) doc.played = mergePlayed(a.played ?? [], b.played ?? []);
  if (a.stickers || b.stickers) doc.stickers = mergeStickers(a.stickers ?? [], b.stickers ?? []);
  return doc;
}

/** Two stored values (JSON text, null = none) joined as unions, as JSON text — the account's merge of the key. */
export function mergePlayDocText(a: string | null | undefined, b: string | null | undefined): string {
  return JSON.stringify(mergePlayDocs(parsePlayDoc(a), parsePlayDoc(b)));
}
